import { useState } from 'react';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { deserialize } from 'serializr';
import { MetaData, OptionsMeta } from 'models/MetaData/metaData.model';
import { generatePath } from 'react-router-dom';
import { removeFalsyKeys } from 'shared/utils/removeFalsyKeys';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';
import { OrganizationDetails } from 'models/OrganizationDetails/organizationDetails.model';
import { serialize } from 'serializr';

enum OrganisationMetaKey {
  ORGANISATIONS_LIST = 'organizations',
  ORGANISATIONS_YEARS = 'organization_years',
}

const OrganisationService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [organisationsList, setOrganisationsList] = useState<MetaData[]>([]);
  const [organisationYearsList, setOrganisationYearsList] = useState<OptionsMeta[]>([]);
  const [organizationDetails, setOrganizationDetails] = useState<OrganizationDetails>();

  const fetchOrganisationListMeta = async () => {
    try {
      setLoading(true);
      const params = { metadata_type: OrganisationMetaKey.ORGANISATIONS_LIST };
      const response = await axiosInstance.get(ApiRoutes.FETCH_META_DATA, {
        params,
      });
      const metaData = deserialize(
        MetaData,
        response.data.data.metadata
      ) as MetaData[];
      setOrganisationsList(metaData);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganisationYearsMeta = async (id?: string) => {
    try {
      setLoading(true);
      const params = {
        metadata_type: OrganisationMetaKey.ORGANISATIONS_YEARS,
        organization_id: id,
      };
      const response = await axiosInstance.get(ApiRoutes.FETCH_META_DATA, {
        params,
      });
      const metaData = deserialize(
        OptionsMeta,
        response.data.data.metadata
      ) as OptionsMeta[];
      setOrganisationYearsList(metaData);
      return metaData;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const validateOrganisationStatus = async (
    params: { year?: number },
    id: number
  ) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.ORGANIZATION_SPECIFIC_VALIDATION, {
        id,
      });
      const response = await axiosInstance.get(url, {
        params,
      });
      return !!response.data.status;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationDetails = async (
    id: string,
    params?: { year?: number }
  ) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        generatePath(ApiRoutes.ORGANIZATION_SPECIFIC, { id }),
        { params }
      );
      const organisationDetails = deserialize(
        OrganizationDetails,
        response.data
      );
      setOrganizationDetails(organisationDetails);
      return true;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const deleteOrganization = async (id: string) => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(
        generatePath(ApiRoutes.ORGANIZATION_SPECIFIC, { id })
      );
      Notification({
        message: response?.data?.message,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      return true;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const editOrganizationDetails = async (
    id: string,
    payload: OrganizationDetails,
    year?: number
  ) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.ORGANIZATION_SPECIFIC, {
        id,
      });
      const serializedPayload = removeFalsyKeys(
        serialize(OrganizationDetails, payload)
      );
      const response = await axiosInstance.put(url, serializedPayload, {
        params: { year },
      });
      Notification({
        message: `${payload.organization?.name} details Updated`,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      const organisationDetails = deserialize(
        OrganizationDetails,
        response.data
      );
      setOrganizationDetails(organisationDetails);
      return true;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    organisationsList,
    organisationYearsList,
    organizationDetails,
    fetchOrganisationListMeta,
    fetchOrganisationYearsMeta,
    validateOrganisationStatus,
    fetchOrganizationDetails,
    deleteOrganization,
    editOrganizationDetails,
  };
};

export default OrganisationService;
